// Hall floor map (backend: HallLayoutService, GET/PUT app/tables/layout/{hall}).
// Everything is in floor units: the floor is floor_width x floor_height, and a
// table's pos_x/pos_y is the top-left corner of its unrotated box (rotation
// turns it around its centre). The cash register draws the same model.

export type TableShape = 'round' | 'square' | 'rectangle';
/** A table, or a counter (bar) whose seats are order places C1, C2... on the register. */
export type TableKind = 'table' | 'counter';

export type FloorTable = {
  id: string;
  code: string;
  title: string;
  pos_x: number | null;
  pos_y: number | null;
  width: number;
  height: number;
  shape: TableShape;
  rotation: number;
  seats: number | null;
  kind: TableKind;
};

export type Floor = {
  id: string;
  title: string;
  floor_width: number;
  floor_height: number;
  tables: FloorTable[];
};

export const DEFAULT_FLOOR = { width: 1000, height: 625 };
export const GRID = 25;
export const SHAPES: TableShape[] = ['round', 'square', 'rectangle'];
/** Chairs given to a table placed without a chair count. */
export const DEFAULT_CHAIRS = 4;
const COUNTER_SIZE = { width: 300, height: 60 };
const DEFAULT_SIZE: Record<TableShape, { width: number; height: number }> = {
  round: { width: 75, height: 75 },
  square: { width: 75, height: 75 },
  rectangle: { width: 150, height: 75 }
};

const num = (value: unknown): number | null => (value === null || value === undefined || value === '' ? null : Number(value));

export function isPlaced(table: FloorTable) {
  return table.pos_x !== null && table.pos_y !== null;
}

export function defaultSize(shape: TableShape) {
  return DEFAULT_SIZE[shape];
}

export function toFloor(raw: Record<string, unknown>): Floor {
  const tables = Array.isArray(raw.tables) ? (raw.tables as Array<Record<string, unknown>>) : [];
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    floor_width: num(raw.floor_width) ?? DEFAULT_FLOOR.width,
    floor_height: num(raw.floor_height) ?? DEFAULT_FLOOR.height,
    tables: tables.map((row) => {
      const kind: TableKind = row.kind === 'counter' ? 'counter' : 'table';
      const shape = kind === 'counter' ? 'rectangle' : SHAPES.includes(row.shape as TableShape) ? (row.shape as TableShape) : 'square';
      const size = kind === 'counter' ? COUNTER_SIZE : DEFAULT_SIZE[shape];
      return {
        id: String(row.id),
        code: String(row.code ?? ''),
        title: String(row.title ?? row.code ?? ''),
        pos_x: num(row.pos_x),
        pos_y: num(row.pos_y),
        width: num(row.width) ?? size.width,
        height: num(row.height) ?? size.height,
        shape,
        rotation: num(row.rotation) ?? 0,
        seats: num(row.seats),
        kind
      };
    })
  };
}

/** A counter's order places, as the register names them: C1, C2... (its code and the seat number). */
export function counterSeatNames(table: FloorTable) {
  return Array.from({ length: Math.max(0, table.seats ?? 0) }, (_, index) => `${table.code}${index + 1}`);
}

/** Where a chair (table, around it) or a stool (counter, along its front) sits, from the object's centre. */
export function seatPositions(table: FloorTable) {
  const seats = Math.min(Math.max(0, table.seats ?? 0), table.kind === 'counter' ? 40 : 24);
  if (table.kind === 'counter') {
    return Array.from({ length: seats }, (_, index) => ({
      x: -table.width / 2 + ((index + 0.5) * table.width) / seats,
      y: table.height / 2 + 26
    }));
  }
  return Array.from({ length: seats }, (_, index) => {
    const angle = (index / seats) * Math.PI * 2 - Math.PI / 2;
    return { x: Math.cos(angle) * (table.width / 2 + 13), y: Math.sin(angle) * (table.height / 2 + 13) };
  });
}

/** Placing a table without a chair count gives it DEFAULT_CHAIRS. */
export function placedAt(table: FloorTable, x: number, y: number): Partial<FloorTable> {
  return { pos_x: x, pos_y: y, ...(table.kind === 'table' && table.seats === null ? { seats: DEFAULT_CHAIRS } : {}) };
}

export function snap(value: number) {
  return Math.round(value / GRID) * GRID;
}

/** Keeps the table's box on the floor. */
export function clampToFloor(floor: Floor, table: FloorTable, x: number, y: number) {
  return {
    x: Math.min(Math.max(0, x), Math.max(0, floor.floor_width - table.width)),
    y: Math.min(Math.max(0, y), Math.max(0, floor.floor_height - table.height))
  };
}

function overlaps(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }) {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** First grid spot, row by row, where the table fits without touching a placed table. */
export function freeSpot(floor: Floor, table: FloorTable) {
  const gap = GRID;
  const placed = floor.tables
    .filter((other) => other.id !== table.id && isPlaced(other))
    .map((other) => ({ x: other.pos_x! - gap, y: other.pos_y! - gap, w: other.width + 2 * gap, h: other.height + 2 * gap }));
  for (let y = gap; y + table.height <= floor.floor_height; y += GRID) {
    for (let x = gap; x + table.width <= floor.floor_width; x += GRID) {
      if (!placed.some((box) => overlaps(box, { x, y, w: table.width, h: table.height }))) return { x, y };
    }
  }
  return { x: 0, y: 0 };
}

export function layoutPayload(floor: Floor) {
  return {
    floor_width: floor.floor_width,
    floor_height: floor.floor_height,
    tables: floor.tables.map((table) => ({
      id: table.id,
      pos_x: table.pos_x,
      pos_y: table.pos_y,
      width: table.width,
      height: table.height,
      shape: table.shape,
      rotation: table.rotation,
      seats: table.seats
    }))
  };
}

/** By name in natural order ("Table 2" before "Table 10"), as on the register. */
export function byTitle(a: FloorTable, b: FloorTable) {
  return a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' });
}
